import React, { useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  ButtonText,
  Center,
  Divider,
  HStack,
  Text,
  VStack,
} from '@gluestack-ui/themed';
import {
  Check, RefreshCw,
} from 'lucide-react-native';
import { useTranslation } from 'react-i18next';
import type { OralExamReadiness as OralExamReadinessType } from '@/common/api/models/oralExamReadiness';
import { OralExamReadiness } from '@/common/api/models/oralExamReadiness';

import {
  canReviewIdInPreflight,
  shouldShowPreflightIdPhotos,
} from '../StudentLiveTile';

import { PREFLIGHT_COLUMN_TITLE_HEIGHT, PreflightColumnLabel } from '../PreflightColumnLabel';

import type { BandState } from './bandState';
import {
  ConfirmedPhotos,
  ID_DOCUMENT_WIDTH,
  IdDocumentPhoto,
  NoPhotoLine,
  PORTRAIT_SIZE,
  SelfiePortraitPhoto,
  ViewPhotosDualZoom,
} from './IdReviewPhotos';
import { JoinGraceLine } from './JoinGraceLine';
import type { IdConsentReviewItem } from './types';

/** Table columns: name | photo | ID | actions. Gaps are `$8`, except `$3` inside the photo pair. */
export const GRID_NAME_WIDTH = 256;
export const GRID_PHOTO_WIDTH = PORTRAIT_SIZE;
export const GRID_ID_WIDTH = ID_DOCUMENT_WIDTH;
export const GRID_COLUMN_GAP = 32;
export const GRID_PHOTO_GAP = 12;
export const GRID_ACTIONS_WIDTH = 168;

/** Fixed table width: the columns and their gaps, no slack. */
export const ID_REVIEW_TABLE_WIDTH = GRID_NAME_WIDTH
  + GRID_COLUMN_GAP
  + GRID_PHOTO_WIDTH
  + GRID_PHOTO_GAP
  + GRID_ID_WIDTH
  + GRID_COLUMN_GAP
  + GRID_ACTIONS_WIDTH;

const ROW_NOWRAP = { flexWrap: 'nowrap' as const };
const STATUS_PART_SEPARATOR = ' · ';
const STATE_DOT_SIZE = 8;
/** Every status line starts after one marker slot, so all status text shares a left edge. */
const STATUS_MARKER_WIDTH = 12;

/** True when a row shows its photo pair (review, waiting, rejected); false when collapsed. */
export function isPhotoBand(bandState: BandState): boolean {
  return bandState.kind !== 'approved' && bandState.kind !== 'registered';
}

function isNeverJoinedReadiness(readiness: OralExamReadinessType): boolean {
  return readiness === OralExamReadiness.not_joined
    || readiness === OralExamReadiness.presence_unknown;
}

function StatusLine({
  marker,
  children,
}: {
  marker?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <HStack space="sm" alignItems="center" style={ROW_NOWRAP}>
      <Center w={STATUS_MARKER_WIDTH} flexShrink={0}>{marker}</Center>
      <Box flexShrink={1}>{children}</Box>
    </HStack>
  );
}

function StateDot({ color }: { color: string }) {
  return (
    <Box
      w={STATE_DOT_SIZE}
      h={STATE_DOT_SIZE}
      borderRadius="$full"
      bg={color}
    />
  );
}

/** Hollow dot: the student is registered but not in the room. */
function AbsentRing() {
  return (
    <Box
      w={STATE_DOT_SIZE}
      h={STATE_DOT_SIZE}
      borderRadius="$full"
      borderWidth={1.5}
      borderColor="$textHint"
    />
  );
}

/**
 * One fact per line at the existing " · " break, so lines never wrap mid-phrase.
 * A row showing its photos drops the photo fact: the photos say it.
 */
function ReviewStatus({ bandState }: { bandState: BandState }) {
  const allParts = bandState.statusLabel.split(STATUS_PART_SEPARATOR);
  const parts = bandState.kind === 'review' ? allParts.slice(0, 1) : allParts;
  return (
    <VStack space="xs" testID={bandState.statusTestId}>
      {parts.map((part, index) => (
        <StatusLine key={part} marker={index === 0 ? <StateDot color={bandState.tone.dotColor} /> : null}>
          <Text size="sm" color="$textBody" numberOfLines={1}>
            {part}
          </Text>
        </StatusLine>
      ))}
    </VStack>
  );
}

/** Identity is shown by the greyed photos and their check; the line says only where the student is. */
function ApprovedStatus({
  bandState, bookingId, isReady,
}: { bandState: BandState; bookingId: string; isReady: boolean }) {
  return (
    <VStack testID={`id-approved-${bookingId}`}>
      <StatusLine marker={<StateDot color={isReady ? '$statusSuccess' : '$textHint'} />}>
        <Text size="sm" color="$textBody" testID={bandState.statusTestId} numberOfLines={1}>
          {bandState.readinessLabel ?? bandState.statusLabel}
        </Text>
      </StatusLine>
    </VStack>
  );
}

type IdReviewBandProps = {
  slotId: string;
  item: IdConsentReviewItem;
  bandState: BandState;
  slotStartIso?: string | null;
  slotEndIso?: string | null;
  pending: boolean;
  onApprove: () => void;
  onRejectOpen: () => void;
  showDivider?: boolean;
  /** First row sits directly under the column header, like the supervisors' first tile. */
  isFirst?: boolean;
};

export function IdReviewBand({
  slotId,
  item,
  bandState,
  slotStartIso,
  slotEndIso,
  pending,
  onApprove,
  onRejectOpen,
  showDivider = true,
  isFirst = false,
}: IdReviewBandProps) {
  const { t } = useTranslation();
  const [viewPhotosOpen, setViewPhotosOpen] = useState(false);
  const { readiness } = item;
  const isApproved = item.idPhotoStatus === 'approved';
  const hasPhoto = item.idPhotoStatus === 'submitted' || isApproved;
  const showIdPhotos = shouldShowPreflightIdPhotos(readiness, item.idPhotoStatus);
  const showReviewActions = canReviewIdInPreflight(readiness);
  const collapsed = !isPhotoBand(bandState);

  const status = (() => {
    if (bandState.kind === 'approved') {
      return (
        <ApprovedStatus
          bandState={bandState}
          bookingId={item.bookingId}
          isReady={readiness === OralExamReadiness.ready_to_start}
        />
      );
    }
    if (bandState.kind === 'registered') {
      return (
        <VStack space="xs">
          <StatusLine marker={<AbsentRing />}>
            <Text size="sm" color="$textSubtle" testID={bandState.statusTestId} numberOfLines={1}>
              {bandState.statusLabel}
            </Text>
          </StatusLine>
          {slotStartIso && slotEndIso && isNeverJoinedReadiness(readiness) ? (
            <StatusLine>
              <JoinGraceLine
                bookingId={item.bookingId}
                slotStartIso={slotStartIso}
                slotEndIso={slotEndIso}
                size="sm"
              />
            </StatusLine>
          ) : null}
        </VStack>
      );
    }
    return <ReviewStatus bandState={bandState} />;
  })();

  const photos = (() => {
    if (bandState.kind === 'approved') {
      return (
        <ConfirmedPhotos
          slotId={slotId}
          bookingId={item.bookingId}
          selfieBlobPath={item.idSelfiePhotoBlobPath}
          documentBlobPath={item.idPhotoBlobPath}
          accessibilityLabel={t('oralExam.lecturer.idReview.viewPhotos', { defaultValue: 'View photos' })}
          gap={GRID_PHOTO_GAP}
          onPress={() => setViewPhotosOpen(true)}
        />
      );
    }
    if (collapsed) return null;
    if (showIdPhotos && hasPhoto) {
      return (
        <>
          <SelfiePortraitPhoto
            slotId={slotId}
            bookingId={item.bookingId}
            blobPath={item.idSelfiePhotoBlobPath}
            studentName={item.studentName}
          />
          <Box w={GRID_PHOTO_GAP} />
          <IdDocumentPhoto
            slotId={slotId}
            bookingId={item.bookingId}
            blobPath={item.idPhotoBlobPath}
            studentName={item.studentName}
          />
        </>
      );
    }
    return <NoPhotoLine bookingId={item.bookingId} gap={GRID_PHOTO_GAP} />;
  })();

  const actions = (() => {
    if (bandState.kind !== 'review' || !showReviewActions) return null;
    return (
      <VStack space="sm">
        <Button
          w="100%"
          onPress={onApprove}
          isDisabled={pending}
          testID={`id-approve-${item.bookingId}`}
        >
          <ButtonIcon as={Check} mr="$2" />
          <ButtonText numberOfLines={1}>
            {t('oralExam.lecturer.idReview.approve', { defaultValue: 'Confirm identity' })}
          </ButtonText>
        </Button>
        <Button
          w="100%"
          variant="outline"
          action="secondary"
          onPress={onRejectOpen}
          isDisabled={pending}
          testID={`id-reject-${item.bookingId}`}
        >
          <ButtonIcon as={RefreshCw} mr="$2" />
          <ButtonText numberOfLines={1}>
            {t('oralExam.lecturer.idReview.reject', { defaultValue: 'Request retake' })}
          </ButtonText>
        </Button>
      </VStack>
    );
  })();

  return (
    <>
      {showDivider ? <Divider bg="$borderLight" /> : null}
      <HStack
        pt={isFirst ? '$0' : '$5'}
        pb="$5"
        alignItems="flex-start"
        style={ROW_NOWRAP}
        testID={`id-review-card-${item.bookingId}`}
      >
        <VStack width={GRID_NAME_WIDTH} flexShrink={0} space="xs">
          <Text
            color="$textHeading"
            fontFamily="$heading"
            numberOfLines={1}
            testID={`id-review-name-${item.bookingId}`}
          >
            {item.studentName}
          </Text>
          {status}
        </VStack>
        <HStack
          ml={GRID_COLUMN_GAP}
          width={GRID_PHOTO_WIDTH + GRID_PHOTO_GAP + GRID_ID_WIDTH}
          flexShrink={0}
          style={ROW_NOWRAP}
        >
          {photos}
        </HStack>
        <Box ml={GRID_COLUMN_GAP} width={GRID_ACTIONS_WIDTH} flexShrink={0}>
          {actions}
        </Box>
      </HStack>
      {bandState.kind === 'approved' ? (
        <ViewPhotosDualZoom
          slotId={slotId}
          bookingId={item.bookingId}
          selfieBlobPath={item.idSelfiePhotoBlobPath}
          documentBlobPath={item.idPhotoBlobPath}
          studentName={item.studentName}
          visible={viewPhotosOpen}
          onClose={() => setViewPhotosOpen(false)}
        />
      ) : null}
    </>
  );
}

/**
 * Column header row above the student rows; `trailing` sits right-aligned over the
 * actions. Photo headers show only while some row has its photos open.
 */
export function IdReviewTableHeader({
  trailing,
  showPhotoHeaders = true,
}: {
  trailing?: React.ReactNode;
  showPhotoHeaders?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <HStack
      h={PREFLIGHT_COLUMN_TITLE_HEIGHT}
      alignItems="center"
      style={ROW_NOWRAP}
      testID="id-review-table-header"
    >
      <Box width={GRID_NAME_WIDTH} flexShrink={0}>
        <PreflightColumnLabel testID="preflight-students-column-title">
          {t('oralExam.lecturer.preflightReview.studentsColumn')}
        </PreflightColumnLabel>
      </Box>
      <Box ml={GRID_COLUMN_GAP} width={GRID_PHOTO_WIDTH} flexShrink={0}>
        {showPhotoHeaders ? (
          <PreflightColumnLabel>
            {t('oralExam.lecturer.idReview.captionPhoto', { defaultValue: 'Student photo' })}
          </PreflightColumnLabel>
        ) : null}
      </Box>
      <Box ml={GRID_PHOTO_GAP} width={GRID_ID_WIDTH} flexShrink={0}>
        {showPhotoHeaders ? (
          <PreflightColumnLabel>
            {t('oralExam.lecturer.idReview.captionIdDocument', { defaultValue: 'ID document' })}
          </PreflightColumnLabel>
        ) : null}
      </Box>
      <Box
        ml={GRID_COLUMN_GAP}
        width={GRID_ACTIONS_WIDTH}
        flexShrink={0}
        alignItems="flex-end"
      >
        {trailing}
      </Box>
    </HStack>
  );
}
